// lib/telefonie/treffer.ts
// Entscheidet, wer angerufen hat, wenn eine Nummer auf mehrere Datensätze passt.
//
// Warum es das braucht (Messung am Bestand vom 2026-08-12): von 637 verschiedenen
// Nummern tragen 438 genau einen Datensatz, 121 mehrere Datensätze DESSELBEN Menschen
// (ein Eigentümer, der zusätzlich als Mieter je Einheit erfasst ist) und nur 78 wirklich
// verschiedene Namen. Wer bei jedem Mehrfachtreffer aufgibt, zeigt bei einem Drittel der
// bekannten Nummern „Unbekannt" an, obwohl die App den Anrufer kennt.

export interface Kandidat {
  owner_typ: string;
  owner_id: string;
  anzeigename: string | null;
  objekt_id: string | null;
}

export interface TrefferWahl {
  /** Gesetzt, wenn eindeutig ein Mensch dahintersteht. */
  treffer: Kandidat | null;
  /** Gesetzt, wenn wirklich verschiedene Namen dahinterstehen — dann fragt die Karte. */
  kandidaten: Kandidat[] | null;
}

const name = (k: Kandidat): string => (k.anzeigename || '').trim().toLowerCase();

/** Rangfolge der Rollen bei Gleichstand: ein Mieter-Datensatz trägt den meisten
 *  Zusammenhang für die Anrufkarte, ein Dienstleister den wenigsten. Ein unbekannter
 *  Typ landet hinter allen bekannten. */
const TYP_RANG: Record<string, number> = {
  mieter: 0,
  eigentuemer: 1,
  kontakt: 2,
  dienstleister: 3,
};
const typRang = (k: Kandidat): number => TYP_RANG[k.owner_typ] ?? 99;

/**
 * Vergleicht zwei Kandidaten desselben Menschen für die Rangfolge. Muss ein
 * ausdrücklicher, von der Eingabereihenfolge unabhängiger Vergleich sein: die
 * Kandidaten kommen aus einer RPC ohne `order by`, deren Zeilenreihenfolge Postgres
 * nicht zusichert. Ein `find()` auf der Eingabereihenfolge würde bei zwei Aufrufen
 * desselben Anrufs unterschiedliche Treffer liefern — genau das, was diese Funktion
 * verhindern soll.
 *
 * Rangfolge, in dieser Reihenfolge angewandt:
 * 1. Objektbezug schlägt keinen Objektbezug.
 * 2. Bei Gleichstand: Rolle nach TYP_RANG (mieter vor eigentuemer vor kontakt vor
 *    dienstleister).
 * 3. Bei weiterhin Gleichstand: owner_id alphabetisch aufsteigend — willkürlich, aber
 *    stabil, und Stabilität ist hier der ganze Punkt.
 */
function vergleicheRang(a: Kandidat, b: Kandidat): number {
  const objektA = a.objekt_id ? 0 : 1;
  const objektB = b.objekt_id ? 0 : 1;
  if (objektA !== objektB) return objektA - objektB;

  const typA = typRang(a);
  const typB = typRang(b);
  if (typA !== typB) return typA - typB;

  return a.owner_id < b.owner_id ? -1 : a.owner_id > b.owner_id ? 1 : 0;
}

/**
 * Kandidaten zu einer Entscheidung verdichten:
 * - keine oder nur namenlose Kandidaten → nichts
 * - alle mit demselben Namen → ein Treffer; die Rangfolge in `vergleicheRang`
 *   entscheidet deterministisch, unabhängig von der Eingabereihenfolge
 * - verschiedene Namen → keine Wahl treffen, alle zur Auswahl zurückgeben
 */
export function waehleTreffer(kandidaten: Kandidat[]): TrefferWahl {
  const leer: TrefferWahl = { treffer: null, kandidaten: null };
  if (!kandidaten || kandidaten.length === 0) return leer;

  const benannt = kandidaten.filter((k) => name(k) !== '');
  if (benannt.length === 0) return leer;

  const namen = new Set(benannt.map(name));
  if (namen.size > 1) return { treffer: null, kandidaten };

  const [treffer] = [...benannt].sort(vergleicheRang);
  return { treffer, kandidaten: null };
}
